import React, { useState } from "react";
import { Film, Play, Building2, Pencil, Eye, Megaphone, Trash2, ShieldCheck, ShieldAlert } from "lucide-react";
import { getVideoPlatformRows, statusLabel, fmtDate } from "../../lib/platformStatus";
import { getVideoCategories } from "../../lib/videoTags";

export default function VideoGrid({ videos, crmActors, onPreview, onEdit, onPlay, onPromo, onPromoV2, onDelete, selectMode, selectedIds, onToggleSelect }) {
  const [unavailablePlaybackIds, setUnavailablePlaybackIds] = useState(() => new Set());
  if (videos.length === 0) {
    return (
      <div className="border border-zinc-900 bg-zinc-950 p-12 text-center text-zinc-600" data-testid="videos-grid-empty">
        No videos match your filters.
      </div>
    );
  }
  return (
    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4" data-testid="videos-grid">
      {videos.map((v) => {
        // Only count prices from platforms actively selling (skip 'presale' and 'none' —
        // those are projections, not real revenue).
        const isSold = (s) => s && s !== "presale" && s !== "none";
        let totalRev = 0;
        if (isSold(v.jff_status))     totalRev += (v.jff_price || 0);
        if (isSold(v.of_vip_status))  totalRev += (v.of_vip_price || 0);
        if (isSold(v.rfc_status))     totalRev += (v.rfc_price || 0);
        if (isSold(v.of_free_status)) totalRev += (v.of_free_sale_price || 0) + (v.of_free_live_price || 0);
        const castPeople = (v.cast_crm_ids || [])
          .map((id) => crmActors.find((a) => a.id === id)).filter(Boolean);
        const platformRows = getVideoPlatformRows(v);
        const categories = getVideoCategories(v.tags);
        const playbackUnavailable = unavailablePlaybackIds.has(v.id);
        const editColor =
          v.edit_status === "edited" ? "#22c55e"
          : v.edit_status === "edit_now" ? "#ef4444"
          : v.edit_status === "semi_edited" ? "#f59e0b"
          : v.edit_status === "free_promo" ? "#06B6D4"
          : v.edit_status === "coming_soon" ? "#fbbf24"
          : v.edit_status === "archived" ? "#71717a"
          : v.edit_status === "no_publishing" ? "#dc2626"
          : "#94a3b8";
        
        const editLabel =
          v.edit_status === "edited" ? "✅ READY"
          : v.edit_status === "ready" ? "✅ READY"
          : v.edit_status === "edit_now" ? "✂️ EDITING"
          : v.edit_status === "semi_edited" ? "✂️ EDITING"
          : v.edit_status === "pending" ? "📹 UNEDITED"
          : v.edit_status === "coming_soon" ? "🟡 COMING SOON"
          : v.edit_status === "free_promo" ? "🆓 FREE PROMO"
          : v.edit_status === "archived" ? "📦 ARCHIVED"
          : v.edit_status === "no_publishing" ? "⛔ NO PUBLISH"
          : v.edit_status?.replace("_", " ").toUpperCase();
        return (
          <div key={v.id} data-testid={`vcard-${v.id}`}
            className={`group relative rounded-xl overflow-hidden border transition-all duration-300 hover:-translate-y-2 hover:shadow-2xl ${
              selectMode && selectedIds?.has(v.id) 
                ? "border-pink-500 bg-gradient-to-br from-purple-900/20 to-pink-900/20 shadow-lg shadow-pink-500/30" 
                : v.is_studio 
                  ? "border-purple-500/40 bg-black/40 backdrop-blur-sm shadow-lg shadow-purple-500/10" 
                  : "border-zinc-800 bg-black/60 backdrop-blur-sm hover:border-purple-500/40"
            }`}>
            {/* Select checkbox (only in select mode) */}
            {selectMode && (
              <button onClick={() => onToggleSelect && onToggleSelect(v.id)}
                data-testid={`vcard-select-${v.id}`}
                className={`absolute top-2 right-2 z-10 w-6 h-6 rounded-lg flex items-center justify-center transition-all ${
                  selectedIds?.has(v.id) 
                    ? "bg-gradient-to-br from-pink-500 to-purple-500 border-2 border-pink-400 shadow-lg shadow-pink-500/50" 
                    : "bg-black/70 backdrop-blur-sm border-2 border-white/50"
                }`}>
                {selectedIds?.has(v.id) && <span className="text-sm font-bold text-white">✓</span>}
              </button>
            )}
            {/* Thumbnail */}
            <button onClick={() => selectMode ? onToggleSelect && onToggleSelect(v.id) : onPreview(v)}
              data-testid={`vcard-thumb-${v.id}`}
              className="relative block w-full aspect-video bg-black overflow-hidden">
              {v.mux_playback_id && !playbackUnavailable ? (
                <img src={`https://image.mux.com/${v.mux_playback_id}/thumbnail.jpg?width=640&height=360&fit_mode=smartcrop&time=2`}
                  alt="" className="w-full h-full object-cover group-hover:scale-105 transition duration-500" loading="lazy"
                  onError={() => setUnavailablePlaybackIds((current) => new Set(current).add(v.id))} />
              ) : v.scene_photos?.[0] ? (
                <img src={v.scene_photos[0]} alt="" className="w-full h-full object-cover opacity-70" loading="lazy" />
              ) : (
                <div className="w-full h-full flex items-center justify-center" style={{ background: "var(--gio-surface-2)" }}>
                  <Film size={32} style={{ color: "var(--gio-muted-3)" }} />
                </div>
              )}
              <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-transparent to-black/30 opacity-0 group-hover:opacity-100 transition" />
              {v.mux_playback_id && !playbackUnavailable && (
                <span role="button" tabIndex={0}
                  onClick={(e) => { e.stopPropagation(); onPlay(v); }}
                  onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.stopPropagation(); e.preventDefault(); onPlay(v); } }}
                  data-testid={`vcard-play-${v.id}`}
                  className="absolute inset-0 m-auto w-14 h-14 rounded-full flex items-center justify-center opacity-0 group-hover:opacity-100 transition cursor-pointer"
                  style={{ background: "rgba(20,184,166,0.9)", color: "#000" }}>
                  <Play size={22} />
                </span>
              )}
              {/* Badges */}
              <div className="absolute top-2 left-2 flex flex-col gap-1">
                <span className="text-[9px] font-bold tracking-wider px-1.5 py-0.5 rounded"
                  style={{ background: `${editColor}dd`, color: "#000" }}>
                  {editLabel}
                </span>
                {v.is_studio && (
                  <span className="text-[9px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded inline-flex items-center gap-1"
                    style={{ background: "rgba(168,85,247,0.9)", color: "#fff" }}>
                    <Building2 size={9} /> STUDIO
                  </span>
                )}
                <span
                  data-testid={`vcard-playback-policy-${v.id}`}
                  title={v.mux_playback_policy === "signed" ? "Full video uses signed playback" : "Full video uses public playback"}
                  className="text-[9px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded inline-flex items-center gap-1"
                  style={v.mux_playback_policy === "signed"
                    ? { background: "rgba(34,197,94,0.9)", color: "#000" }
                    : { background: "rgba(245,158,11,0.9)", color: "#000" }}>
                  <ShieldCheck size={9} /> {v.mux_playback_policy === "signed" ? "SIGNED FULL" : "PUBLIC FULL"}
                </span>
                {/* Legal badge — shows for no_publishing videos (compliance flag) */}
                {v.edit_status === "no_publishing" && (
                  (v.legal_docs || []).length > 0 || v.legal_notes || v.studio_contract_url ? (
                    <span className="text-[9px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded inline-flex items-center gap-1"
                      title="Legal docs on file"
                      data-testid={`vcard-legal-ok-${v.id}`}
                      style={{ background: "rgba(34,197,94,0.9)", color: "#000" }}>
                      <ShieldCheck size={9} /> LEGAL OK
                    </span>
                  ) : (
                    <span className="text-[9px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded inline-flex items-center gap-1"
                      title="No release docs — add legal on file"
                      data-testid={`vcard-legal-missing-${v.id}`}
                      style={{ background: "rgba(220,38,38,0.95)", color: "#fff" }}>
                      <ShieldAlert size={9} /> LEGAL MISSING
                    </span>
                  )
                )}
              </div>
              {totalRev > 0 && (
                <span className="absolute top-2 right-2 text-[10px] font-bold px-1.5 py-0.5 rounded"
                  style={{ background: "rgba(34,197,94,0.9)", color: "#000" }}>
                  ${totalRev.toFixed(0)}
                </span>
              )}
            </button>

            {/* Body - Enhanced with more details - BIGGER & MORE VISIBLE */}
            <div className="p-4 space-y-3">
              <h3 className="font-heading text-lg font-bold leading-tight line-clamp-2 text-white" title={v.title} data-testid={`vcard-title-${v.id}`}>
                {v.title}
              </h3>
              
              {/* Enhanced metadata row - BIGGER */}
              <div className="flex items-center gap-2 flex-wrap text-sm text-zinc-300">
                {v.city && <span className="inline-flex items-center gap-1 font-semibold text-purple-400">📍 {v.city}</span>}
                {v.shoot_date && <span className="font-medium">· {v.shoot_date}</span>}
                {v.runtime_minutes && <span className="font-medium">· {v.runtime_minutes}min</span>}
              </div>
              
              {/* Location if different from city */}
              {v.location && (
                <div className="text-sm text-zinc-400 italic truncate">
                  {v.location}
                </div>
              )}
              {castPeople.length > 0 && (
                <div className="flex flex-wrap gap-1.5">
                  {castPeople.slice(0, 3).map((p) => (
                    <span key={p.id} className="inline-flex items-center gap-1.5 px-2 py-1 rounded-md text-xs font-semibold bg-purple-900/30 text-purple-300 border border-purple-500/30">
                      {p.photos?.[0] && <img src={p.photos[0]} alt="" className="w-4 h-4 rounded-full object-cover" />}
                      {p.stage_name}
                    </span>
                  ))}
                  {castPeople.length > 3 && (
                    <span className="text-sm text-zinc-400">+{castPeople.length - 3}</span>
                  )}
                </div>
              )}
              {/* Mux status indicator */}
              {v.mux_status && v.mux_duration && (
                <div className="text-xs flex items-center gap-2 text-cyan-400 font-semibold">
                  <span className="w-2 h-2 rounded-full bg-cyan-400 animate-pulse"></span>
                  <span>Mux: {Math.round(v.mux_duration)}s</span>
                </div>
              )}
              
              {v.status_comment && (
                <div className="text-sm italic px-3 py-2 rounded-lg border line-clamp-2 bg-amber-900/10 text-amber-300 border-amber-500/30 font-medium">
                  {v.status_comment}
                </div>
              )}
              {/* Platform Matrix Status - reads platform_status (Matrix) with legacy fallback */}
              {(() => {
                const platformStatuses = platformRows;
                if (platformStatuses.length === 0) return null;

                return (
                  <div>
                    <div className="text-xs font-semibold text-purple-400 mb-1.5">Platform Status</div>
                    <div className="flex flex-wrap gap-2" data-testid={`vcard-platforms-${v.id}`}>
                      {platformStatuses.map((p) => (
                        <span 
                          key={p.key} 
                          title={`${p.label}: ${statusLabel(p.status)}${p.posted_date ? ` (${fmtDate(p.posted_date)})` : ''}`}
                          className="text-xs font-mono uppercase font-bold px-2.5 py-1.5 rounded-lg border-2 backdrop-blur-sm transition-all hover:scale-105 flex items-center gap-1"
                          style={{ 
                            background: `${p.color}20`, 
                            color: p.color, 
                            borderColor: `${p.color}60` 
                          }}>
                          <span>{p.label}</span>
                          {p.posted_date && (
                            <span className="opacity-80 text-[11px] font-normal normal-case">
                              {fmtDate(p.posted_date)}
                            </span>
                          )}
                          <span className="text-[9px] opacity-80 normal-case">
                            {p.status === "published" || p.status === "re_published" ? "✓" : 
                             p.status === "live_on_subs" || p.status === "live_sub" ? "🔴" : 
                             p.status === "scheduled" || p.status === "to_be_scheduled" ? "⏰" : 
                             p.status === "uploaded" ? "⬆️" :
                             p.status === "promo_posted" ? "📣" : "•"}
                          </span>
                        </span>
                      ))}
                    </div>
                  </div>
                );
              })()}

              {/* Tags - BIGGER */}
              {categories.length > 0 && (
                <div className="flex flex-wrap gap-1.5">
                  {categories.map((tag) => (
                    <span 
                      key={tag}
                      className="text-xs px-2.5 py-1 rounded-full bg-purple-900/30 text-purple-300 border border-purple-500/30 font-medium">
                      #{tag}
                    </span>
                  ))}
                </div>
              )}
              <div className="flex items-center justify-between gap-1 pt-1">
                <div className="flex flex-wrap gap-1">
                  {v.scene_photos?.length > 0 && (
                    <span className="text-[9px] font-mono-ui inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded"
                      style={{ background: "var(--gio-surface-2)", color: "var(--gio-muted)" }}>
                      📷 {v.scene_photos.length}
                    </span>
                  )}
                  {v.trailer_mux_playback_id && (
                    <span className="text-[9px] font-mono-ui inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded"
                      style={{ background: "rgba(6,182,212,0.15)", color: "#06B6D4" }}>
                      🎬 trailer
                    </span>
                  )}
                  {v.extra_videos?.length > 0 && (
                    <span className="text-[9px] font-mono-ui inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded"
                      style={{ background: "rgba(168,85,247,0.15)", color: "#a855f7" }}>
                      🎬+{v.extra_videos.length}
                    </span>
                  )}
                </div>
                <div className="flex items-center gap-1">
                  <button onClick={(e) => { e.stopPropagation(); onPreview(v); }} data-testid={`vcard-preview-${v.id}`} title="Preview"
                    className="p-1.5 rounded hover:bg-zinc-800 text-zinc-400 hover:text-[color:var(--gio-secondary)]">
                    <Eye size={13} />
                  </button>
                  <button onClick={(e) => { e.stopPropagation(); onEdit(v); }} data-testid={`vcard-edit-${v.id}`} title="Manage Video"
                    className="inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded bg-cyan-900/30 border border-cyan-700/50 text-cyan-200 hover:bg-cyan-800/40 text-xs font-semibold">
                    <Pencil size={13} /> Manage Video
                  </button>
                  {onPromoV2 && (
                    <button onClick={() => onPromoV2(v)} data-testid={`vcard-promo-v2-${v.id}`} title="Promo Pack V2 (multi-step)"
                      className="p-1.5 rounded hover:bg-zinc-800 text-zinc-400 hover:text-purple-400 relative">
                      <Megaphone size={13} />
                      <span className="absolute -top-1 -right-1 text-[7px] font-bold bg-purple-500 text-white px-1 rounded">V2</span>
                    </button>
                  )}
                  {onDelete && (
                    <button onClick={() => onDelete(v)} data-testid={`vcard-delete-${v.id}`} title="Delete video"
                      className="p-1.5 rounded hover:bg-red-900/40 text-zinc-400 hover:text-red-400">
                      <Trash2 size={13} />
                    </button>
                  )}
                </div>
              </div>
            </div>
          </div>
        );
      })}
    </div>
  );
}
